package com.tovtalk.ui.theme

import androidx.compose.runtime.Immutable
import androidx.compose.runtime.staticCompositionLocalOf

/**
 * TovTalk Opacity System - Standardized opacity/alpha values
 * Used for overlays, disabled states, shimmer effects, scrim, etc.
 */
@Immutable
data class TovTalkOpacitySystem(
    /** 0.03f - Barely visible tint, subtle list backgrounds */
    val faintest: Float,
    /** 0.04f - Very faint tint */
    val ultraFaint: Float,
    /** 0.05f - Faint surface tint, hover states */
    val faint: Float,
    /** 0.06f - Very subtle badge/indicator backgrounds */
    val whisper: Float,
    /** 0.08f - Subtle container highlights */
    val hint: Float,
    /** 0.1f - Soft divider, light surface accent */
    val soft: Float,
    /** 0.12f - Button tint, chip backgrounds */
    val gentle: Float,
    /** 0.15f - Notification/badge backgrounds */
    val mild: Float,
    /** 0.2f - Stronger tint, secondary backgrounds */
    val moderate: Float,
    /** 0.3f - Skeleton shimmer low, subtle backgrounds */
    val subtle: Float,
    /** 0.35f - Light overlays, faint indicators */
    val light: Float,
    /** 0.4f - Drag handles, medium-light overlays */
    val medium: Float,
    /** 0.45f - Scrim backgrounds, comment dividers */
    val semiStrong: Float,
    /** 0.5f - Half opacity, disabled content */
    val half: Float,
    /** 0.6f - Strong overlays, secondary content on video */
    val strong: Float,
    /** 0.7f - Skeleton shimmer high, heavy overlays */
    val heavy: Float,
    /** 0.75f - Music text on video */
    val prominent: Float,
    /** 0.8f - Very visible overlay */
    val dominant: Float,
    /** 0.85f - Near-opaque overlays, video backgrounds */
    val intense: Float,
    /** 0.88f - Very near-opaque, blur backgrounds */
    val nearOpaque: Float,
    /** 0.9f - Almost fully opaque */
    val opaque: Float,
    /** 0.95f - Nearly invisible transparency, text on dark */
    val nearFull: Float,
)

val TovTalkOpacity = TovTalkOpacitySystem(
    faintest = 0.03f,
    ultraFaint = 0.04f,
    faint = 0.05f,
    whisper = 0.06f,
    hint = 0.08f,
    soft = 0.1f,
    gentle = 0.12f,
    mild = 0.15f,
    moderate = 0.2f,
    subtle = 0.3f,
    light = 0.35f,
    medium = 0.4f,
    semiStrong = 0.45f,
    half = 0.5f,
    strong = 0.6f,
    heavy = 0.7f,
    prominent = 0.75f,
    dominant = 0.8f,
    intense = 0.85f,
    nearOpaque = 0.88f,
    opaque = 0.9f,
    nearFull = 0.95f,
)

val LocalTovTalkOpacity = staticCompositionLocalOf { TovTalkOpacity }
